import React, {
  Dispatch,
  FC,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import Compressor from 'compressorjs'
import * as faceapi from 'face-api.js'
import {showError} from '../utils/toast'

type Props = {
  isCaptured: boolean
  setIsCaptured: Dispatch<SetStateAction<boolean>>
  setImage: Dispatch<SetStateAction<File | Blob | undefined>>
  setCanCapture?: Dispatch<SetStateAction<boolean>>
  useFaceDetection?: boolean
  width?: number
  height?: number
  onError(error: Error): void
  autoTake?: boolean
  setIsFaceDetected?: Dispatch<SetStateAction<boolean>>
  setIsLoadingFaceDetection?: Dispatch<SetStateAction<boolean>>
  setRealtimeHint?: Dispatch<SetStateAction<string>>
  onCheckStateChange?: (state: CameraCheckState, message: string) => void
  detectionStableFrames?: number
  minFaceCoverage?: number
  detectionIntervalMs?: number
}

type CameraCheckState =
  | 'idle'
  | 'initializing'
  | 'realtime_checking'
  | 'captured_validating'
  | 'ready'
  | 'failed'

type RealtimeDetectionReason =
  | 'no_face'
  | 'face_too_far'
  | 'face_off_center'
  | 'face_not_full'
  | 'valid'

const CameraCapture: FC<Props> = ({
  isCaptured = false,
  autoTake = false,
  setIsCaptured,
  setImage,
  setCanCapture,
  useFaceDetection = false,
  onError,
  width = 300,
  height = 300,
  setIsFaceDetected = null,
  setIsLoadingFaceDetection = null,
  setRealtimeHint = null,
  onCheckStateChange = null,
  detectionStableFrames = 3,
  minFaceCoverage = 0.08,
  detectionIntervalMs = 250,
}) => {
  const videoRef: MutableRefObject<HTMLVideoElement | null> = useRef(null)
  const canvasRef: MutableRefObject<HTMLCanvasElement | null> = useRef(null)
  const overlayRef: MutableRefObject<HTMLCanvasElement | null> = useRef(null)
  const [imageData, setImageData] = useState<string | null>(null)
  const [hidePreview, setHidePreview] = useState(false)
  const [modelsLoaded, setModelsLoaded] = useState(false)
  const isDetectingRef = useRef(false)
  const stableDetectionCountRef = useRef(0)
  const modelLoadPromiseRef = useRef<Promise<void> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const isMountedRef = useRef(true)
  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const analysisContextRef = useRef<CanvasRenderingContext2D | null>(null)

  const clearOverlay = () => {
    const context = overlayRef.current?.getContext('2d')
    if (context && overlayRef.current) {
      context.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height)
    }
  }

  const setHint = (message: string) => {
    if (setRealtimeHint) {
      setRealtimeHint(message)
    }
  }

  const setCheckState = (state: CameraCheckState, message: string) => {
    if (onCheckStateChange) {
      onCheckStateChange(state, message)
    }
  }

  const ensureModelsLoaded = async () => {
    if (modelsLoaded) return
    if (modelLoadPromiseRef.current) {
      await modelLoadPromiseRef.current
      return
    }

    setCheckState('initializing', 'Menyiapkan face detector...')

    modelLoadPromiseRef.current = Promise.all([
      faceapi.nets.ssdMobilenetv1.loadFromUri('/models'),
      faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
    ])
      .then(() => {
        setModelsLoaded(true)
      })
      .catch((err) => {
        setCheckState('failed', 'Face detector gagal dimuat.')
        onError(err)
      })
      .finally(() => {
        modelLoadPromiseRef.current = null
      })

    await modelLoadPromiseRef.current
  }

  const startCamera = () => {
    setHidePreview(false)
    stableDetectionCountRef.current = 0
    stopCamera()
    setCheckState('initializing', 'Menyiapkan kamera...')

    if (setCanCapture) {
      setCanCapture(false)
    }

    navigator.mediaDevices
      .getUserMedia({video: {width, height, aspectRatio: 1}})
      .then((stream) => {
        if (!isMountedRef.current) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = streamRef.current
          videoRef.current.play().catch(() => {})
        }
        if (setCanCapture) {
          setCanCapture(true)
        }
        setHint('')
        setCheckState(
          useFaceDetection ? 'realtime_checking' : 'ready',
          useFaceDetection ? 'Mendeteksi wajah...' : 'Kamera siap.'
        )
      })
      .catch((err) => {
        console.error('Error accessing camera: ', err)
        setHint('Kamera gagal diakses. Periksa izin kamera browser.')
        setCheckState('failed', 'Kamera gagal diakses.')
        onError(err)
      })
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream
      const tracks = stream.getTracks()
      tracks.forEach((track) => track.stop())
      videoRef.current.srcObject = null
    }
  }

  const detectWithFallback = async (
    input: HTMLVideoElement | HTMLCanvasElement
  ): Promise<faceapi.FaceDetection | null> => {
    try {
      const detections = await faceapi.detectAllFaces(
        input,
        new faceapi.TinyFaceDetectorOptions({inputSize: 320, scoreThreshold: 0.25})
      )
      if (detections.length > 0) {
        return detections.sort((a, b) => b.box.area - a.box.area)[0]
      }
    } catch (e) {}

    try {
      const detections = await faceapi.detectAllFaces(
        input,
        new faceapi.SsdMobilenetv1Options({minConfidence: 0.3})
      )
      if (detections.length > 0) {
        return detections.sort((a, b) => b.box.area - a.box.area)[0]
      }
    } catch (e) {}

    return null
  }

  const isFaceValid = (
    detection: faceapi.FaceDetection | null,
    frameWidth: number,
    frameHeight: number
  ): boolean => {
    if (!detection) return false
    const faceArea = detection.box.width * detection.box.height
    const frameArea = frameWidth * frameHeight
    if (!frameArea) return false
    const coverage = faceArea / frameArea
    return coverage >= minFaceCoverage
  }

  const evaluateRealtimeFace = (
    detection: faceapi.FaceDetection | null
  ): {reason: RealtimeDetectionReason; valid: boolean} => {
    if (!detection) {
      return {reason: 'no_face', valid: false}
    }

    const faceCoverage = (detection.box.width * detection.box.height) / (width * height)
    const cx = detection.box.x + detection.box.width / 2
    const cy = detection.box.y + detection.box.height / 2
    const centered =
      Math.abs(cx - width / 2) <= width * 0.22 && Math.abs(cy - height / 2) <= height * 0.22
    const edgeMargin = Math.min(width, height) * 0.08
    const notFullFace =
      detection.box.x <= edgeMargin ||
      detection.box.y <= edgeMargin ||
      detection.box.x + detection.box.width >= width - edgeMargin ||
      detection.box.y + detection.box.height >= height - edgeMargin

    if (notFullFace) {
      return {reason: 'face_not_full', valid: false}
    }

    if (!centered) {
      return {reason: 'face_off_center', valid: false}
    }

    if (faceCoverage < 0.06 || !isFaceValid(detection, width, height)) {
      return {reason: 'face_too_far', valid: false}
    }

    return {reason: 'valid', valid: true}
  }

  const drawFaceOverlay = (detection: faceapi.FaceDetection | null, isStable: boolean) => {
    if (!overlayRef.current) return
    const context = overlayRef.current.getContext('2d')
    if (!context) return

    overlayRef.current.width = width
    overlayRef.current.height = height
    context.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height)

    if (!detection) {
      // Always show a guide circle so low-end devices don't look "stuck".
      const r = Math.min(width, height) * 0.28
      context.beginPath()
      context.arc(width / 2, height / 2, r, 0, 2 * Math.PI)
      context.lineWidth = 2
      context.setLineDash([6, 6])
      context.strokeStyle = '#9ca3af'
      context.stroke()
      context.setLineDash([])
      return
    }

    const {x, y, width: w, height: h} = detection.box
    context.beginPath()
    context.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI)
    context.lineWidth = 2
    context.strokeStyle = isStable ? '#22c55e' : '#2563eb'
    context.stroke()
  }

  const getBrightnessHint = (): string => {
    if (!videoRef.current) return ''
    if (!analysisCanvasRef.current) {
      analysisCanvasRef.current = document.createElement('canvas')
      analysisCanvasRef.current.width = 48
      analysisCanvasRef.current.height = 48
      analysisContextRef.current = analysisCanvasRef.current.getContext('2d')
    }

    const ctx = analysisContextRef.current
    if (!ctx) return ''
    ctx.drawImage(videoRef.current, 0, 0, 48, 48)
    const pixels = ctx.getImageData(0, 0, 48, 48).data
    let sum = 0
    for (let i = 0; i < pixels.length; i += 4) {
      // Relative luminance approximation.
      sum += 0.2126 * pixels[i] + 0.7152 * pixels[i + 1] + 0.0722 * pixels[i + 2]
    }
    const avg = sum / (pixels.length / 4)
    if (avg < 55) return 'Cahaya kurang. Tambahkan lampu agar wajah terbaca.'
    return ''
  }

  const detectFace = async () => {
    if (!videoRef.current || !overlayRef.current || !modelsLoaded || isDetectingRef.current) return

    isDetectingRef.current = true
    try {
      const detection = await detectWithFallback(videoRef.current)
      const evaluated = evaluateRealtimeFace(detection)
      const valid = evaluated.valid

      stableDetectionCountRef.current = valid ? stableDetectionCountRef.current + 1 : 0
      const isStable = stableDetectionCountRef.current >= detectionStableFrames

      drawFaceOverlay(detection, isStable)

      if (autoTake && isStable) {
        setIsCaptured(true)
      }

      if (evaluated.reason === 'valid') {
        setHint('')
        setCheckState('ready', 'Wajah terdeteksi.')
      } else if (evaluated.reason === 'face_not_full') {
        setHint('Wajah belum terlihat penuh. Hadapkan wajah lurus ke kamera.')
        setCheckState(
          'realtime_checking',
          'Wajah belum terlihat penuh. Hadapkan wajah lurus ke kamera.'
        )
      } else if (evaluated.reason === 'face_off_center') {
        setHint('Posisikan wajah di tengah lingkaran.')
        setCheckState('realtime_checking', 'Posisikan wajah di tengah lingkaran.')
      } else if (evaluated.reason === 'face_too_far') {
        setHint('Wajah terlalu jauh. Dekatkan wajah ke kamera.')
        setCheckState('realtime_checking', 'Wajah terlalu jauh. Dekatkan wajah ke kamera.')
      } else {
        setHint(getBrightnessHint() || 'Wajah belum terdeteksi. Arahkan wajah ke kamera.')
        setCheckState('realtime_checking', 'Mendeteksi wajah...')
      }

      // Keep capture enabled once camera is ready. Strict validation stays on Start button.
      if (setCanCapture) {
        setCanCapture(true)
      }
    } finally {
      isDetectingRef.current = false
    }
  }

  const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png')
    })
  }

  const captureImage = async () => {
    if (canvasRef.current && videoRef.current) {
      const context = canvasRef.current.getContext('2d')
      if (context) {
        context.save()
        context.translate(canvasRef.current.width, 0)
        context.scale(-1, 1)
        context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height)
        context.restore()
        const imagePreview = canvasRef.current.toDataURL('image/png')
        setImageData(imagePreview)
        setHidePreview(true)
        stopCamera()
        clearOverlay()

        const rawBlob = await canvasToBlob(canvasRef.current)
        if (!rawBlob) {
          onError(new Error('Failed to capture image from camera'))
          return
        }

        if (setIsLoadingFaceDetection) {
          setIsLoadingFaceDetection(true)
        }
        setCheckState('captured_validating', 'Memvalidasi foto...')

        try {
          await ensureModelsLoaded()
          const detection = await detectWithFallback(canvasRef.current)
          const valid = isFaceValid(detection, width, height)

          if (setIsFaceDetected) {
            setIsFaceDetected(valid)
          }

          if (!valid) {
            setCheckState('failed', 'Wajah belum terdeteksi jelas. Silakan retake.')
            showError('Wajah belum terdeteksi dengan jelas. Silakan retake.')
          } else {
            setCheckState('ready', 'Wajah terdeteksi.')
          }
        } finally {
          if (setIsLoadingFaceDetection) {
            setIsLoadingFaceDetection(false)
          }
        }

        new Compressor(rawBlob, {
          quality: 0.6,
          success(compressedResult) {
            setImage(compressedResult)
          },
          error() {
            setImage(rawBlob)
          },
        })
      }
    }
  }

  useEffect(() => {
    ensureModelsLoaded()
  }, [])

  useEffect(() => {
    if (!isCaptured) {
      startCamera()
      if (setIsFaceDetected) {
        setIsFaceDetected(false)
      }
    }
  }, [isCaptured, width, height])

  useEffect(() => {
    if (isCaptured) {
      captureImage()
    }
  }, [isCaptured])

  useEffect(() => {
    if (isCaptured) return

    const interval = setInterval(() => {
      if (useFaceDetection) {
        if (!modelsLoaded) {
          setHint('Menyiapkan face detector...')
          setCheckState('initializing', 'Menyiapkan face detector...')
          return
        }

        detectFace()
        return
      }

      setHint('')
      setCheckState('ready', 'Kamera siap.')
      if (setCanCapture) {
        setCanCapture(true)
      }
    }, detectionIntervalMs)

    return () => clearInterval(interval)
  }, [isCaptured, useFaceDetection, modelsLoaded, detectionIntervalMs])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      setCheckState('idle', '')
      stopCamera()
    }
  }, [])

  return (
    <div className='container text-center mt-5' style={{width, height}}>
      <div className='mb-3' style={{position: 'relative', width, height}}>
        {hidePreview && (
          <img
            src={imageData || ''}
            alt='Captured'
            className='rounded-circle'
            width={width}
            height={height}
          />
        )}
        {!hidePreview && (
          <>
            <video
              ref={videoRef}
              autoPlay
              className='rounded-circle'
              style={{
                width: `${width}px`,
                height: `${height}px`,
                objectFit: 'cover',
                transform: 'scaleX(-1)',
              }}
            ></video>
            <canvas
              ref={overlayRef}
              className='rounded-circle'
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${width}px`,
                height: `${height}px`,
                pointerEvents: 'none',
                transform: 'scaleX(-1)',
              }}
            ></canvas>
          </>
        )}
        <canvas ref={canvasRef} width={width} height={height} style={{display: 'none'}}></canvas>
      </div>
    </div>
  )
}

export default CameraCapture
